/**
 * User Roles Utility Functions
 * 
 * Provides utility functions for checking user roles and permissions:
 * - Role checking
 * - Permission checking
 * - Role hierarchy management
 * 
 * @module utils/userRoles
 */

/**
 * Checks if a user has a specific role
 * 
 * @param {Object} user - User object
 * @param {string} role - Role to check
 * @returns {boolean} True if user has the role, false otherwise
 */
export const hasRole = (user, role) => {
  if (!user) return false;
  
  if (user.permissions && Array.isArray(user.permissions)) {
    return user.permissions.includes(role);
  }
  
  if (user.role && typeof user.role === 'string') {
    return user.role === role;
  }
  
  return false;
};

/**
 * Checks if a user has a specific permission
 * 
 * Checks both direct permissions and role-based permissions.
 * Administrators have all permissions.
 * 
 * @param {Object} user - User object
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has the permission, false otherwise
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  const userRoles = user.permissions && Array.isArray(user.permissions) ? user.permissions : 
                   user.role && typeof user.role === 'string' ? [user.role] : [];
  
  // Administrator has all permissions
  if (userRoles.includes('admin')) return true;
  
  // Check direct permissions
  if (user.permissions?.includes(permission)) return true;
  
  const rolePermissions = {
    admin: ['all'],
    manager: ['read', 'write', 'manage_team', 'reports', 'analytics'],
    operator: ['read', 'warehouse', 'picking', 'inventory_items', 'packing'],
    client: ['read', 'orders', 'tracking', 'analytics', 'billing'],
    viewer: ['read', 'orders', 'tracking', 'analytics', 'billing'], // Add viewer role
    selfservice: ['read_basic', 'orders_basic', 'tracking_basic', 'fba'],
    warehouse: ['read', 'write', 'manage_warehouse', 'reports', "warehouse_locations"],
  };

  for (const role of userRoles) {
    const permissions = rolePermissions[role] || [];
    if (permissions.includes(permission) || permissions.includes('all')) {
      return true;
    }
  }

  return false;
};

/**
 * Gets all permissions of a user
 */
export const getUserPermissions = (user) => {
  if (!user) return [];
  
  const permissions = new Set();
  
  // Add direct permissions
  if (user.permissions) {
    user.permissions.forEach(p => permissions.add(p));
  }
  
  // Add role-based permissions
  const rolePermissions = {
    admin: ['all'],
    manager: ['read', 'write', 'manage_team', 'reports', 'analytics'],
    operator: ['read', 'warehouse', 'picking', 'inventory_items', 'packing'],
    client: ['read', 'orders', 'tracking', 'analytics', 'billing'],
    selfservice: ['read_basic', 'orders_basic', 'tracking_basic', 'fba'],
    warehouse: ['read', 'write', 'manage_warehouse', 'reports', "warehouse_locations"],
  };

  if (user.permissions) {
    user.permissions.forEach(role => {
      const perms = rolePermissions[role] || [];
      perms.forEach(p => permissions.add(p));
    });
  }
  
  return Array.from(permissions);
};

/**
 * Checks if a user can access a resource
 */
export const canAccessResource = (
  user, 
  requiredRoles = [], 
  requiredPermissions = []
) => {
  if (!user) return false;
  
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(user, role));
    if (!hasRequiredRole) return false;
  }
  
  // Check required permissions
  if (requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.some(permission => 
      hasPermission(user, permission)
    );
    if (!hasRequiredPermission) return false;
  }
  
  return true;
};

/**
 * Gets the default dashboard according to the role
 */
export const getDefaultDashboard = (user) => {
  if (!user) return '/login';
  
  const userRoles = user.permissions && Array.isArray(user.permissions) ? user.permissions : 
                   user.role && typeof user.role === 'string' ? [user.role] : [];
  
  if (userRoles.length === 0) {
    return '/login';
  }

  const primaryRole = userRoles[0];
  
  const dashboardRoutes = {
    admin: '/admin/dashboard',
    manager: '/manager/dashboard',
    operator: '/operator/dashboard',
    client: '/client/dashboard',
    viewer: '/client/dashboard',
    selfservice: '/fba/dashboard',
    warehouse: '/warehouse/dashboard',
  };

  return dashboardRoutes[primaryRole] || '/dashboard';
};

/**
 * Gets the accessible routes by role
 */
export const getAccessibleRoutes = (user) => {
  if (!user || !user.permissions) return [];

  const routesByRole = {
    admin: [
      '/admin/dashboard',
      '/admin/users',
      '/admin/orders',
      '/admin/products',
      '/admin/warehouses',
      '/admin/reports',
      '/admin/settings',
      '/admin/monitoring',
      '/admin/integrations'
    ],
    manager: [
      '/admin/dashboard',
      '/admin/orders',
      '/admin/reports',
      '/admin/team',
      '/admin/analytics'
    ],
    operator: [
      '/operator/home',
      '/operator/inventory',
      '/operator/picking',
      '/operator/packing',
      '/operator/locations'
    ],
    client: [
      '/client/dashboard',
      '/client/orders',
      '/client/inventory',
      '/client/billing',
      '/client/support'
    ],
    selfservice: [
      '/fba/dashboard',
      '/fba/shipments',
      '/fba/inventory',
      '/fba/reports',
      '/fba/settings'
    ],
    warehouse: [
      '/warehouse/dashboard',
      '/warehouse/locations',
      '/warehouse/inventory',
      '/warehouse/reports'
    ]
  };

  const accessibleRoutes = new Set();
  
  user.permissions.forEach(role => {
    const routes = routesByRole[role] || [];
    routes.forEach(route => accessibleRoutes.add(route));
  });

  return Array.from(accessibleRoutes);
};

/**
 * Role constants
 */
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager', 
  OPERATOR: 'operator',
  CLIENT: 'client',
  SELFSERVICE: 'selfservice',
  WAREHOUSE: 'warehouse'
};

/**
 * Role labels for display
 */
export const ROLE_LABELS = {
  admin: 'Administrator',
  manager: 'Manager',
  operator: 'Operator',
  client: 'Client',
  selfservice: 'Amazon FBA',
  warehouse: 'Warehouse Manager'
};

